import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportTicketGitHubState } from '../../../models/Support/ISupportGitHub';
import { GitHubGlyph } from './inboxIcons';
import { ticketTableStyles as ts } from './styles/ticketTableStyles';

/**
 * Where a ticket's escalation stands, as one glyph on the row. The colour is the fast read a desk
 * scans for — yellow for the triage repo, blue once engineering owns it — and every marker also
 * carries the same state as text, since colour alone is no signal to a colour-blind agent.
 */
const MARKER_LABEL: Record<ISupportTicketGitHubState['kind'], string> = {
	Triage: 'Escalated to Leviosa',
	Engineering: 'Escalated to engineering',
	Generic: 'Linked to a GitHub issue',
};

function getMarkerStyle(state: ISupportTicketGitHubState) {
	if (state.state === 'Closed') return ts.gitHubMarkerClosed;
	if (state.kind === 'Engineering') return ts.gitHubMarkerEngineering;
	if (state.kind === 'Triage') return ts.gitHubMarkerTriage;
	return ts.gitHubMarkerGeneric;
}

function getMarkerLabel(state: ISupportTicketGitHubState): string {
	const stage = MARKER_LABEL[state.kind] ?? MARKER_LABEL.Generic;
	const issues = state.issueCount > 1 ? ` (${state.issueCount} issues)` : '';
	return state.state === 'Closed' ? `${stage} — issue closed${issues}` : `${stage}${issues}`;
}

interface TicketGitHubMarkerProps {
	state?: ISupportTicketGitHubState;
}

export const TicketGitHubMarker: React.FC<TicketGitHubMarkerProps> = ({ state }) => {
	if (!state) return null;

	const label = getMarkerLabel(state);
	const glyph = <GitHubGlyph className={css(ts.gitHubMarkerGlyph)} size={14} />;

	// The row's own click opens the ticket, so the marker only steals the click when it has somewhere
	// else to go — straight to the issue the agent would otherwise scroll the thread to find.
	if (!state.url) {
		return (
			<span className={css(ts.gitHubMarker, getMarkerStyle(state))} title={label} aria-label={label} role='img'>
				{glyph}
			</span>
		);
	}

	return (
		<a
			className={css(ts.gitHubMarker, ts.gitHubMarkerLink, getMarkerStyle(state))}
			href={state.url}
			target='_blank'
			rel='noopener noreferrer'
			title={`${label}${state.repository ? ` — ${state.repository}` : ''}`}
			aria-label={label}
			onClick={event => event.stopPropagation()}
		>
			{glyph}
		</a>
	);
};
